import * as yup from 'yup';
import type { LocationQueryValue } from 'vue-router';

/**
 * Extracts the top-level domain (TLD) from a given hostname string. This function
 * first removes any port number from the hostname, if present. It then analyzes
 * the hostname structure to determine and return the TLD. For hostnames representing
 * standard domain formats, it returns the last two components as the TLD. If the
 * hostname does not follow a conventional domain structure, it returns the hostname
 * without the port number.
 *
 * The function assumes a simplified model where the TLD for standard domains is always
 * the last two parts of the domain name (e.g., example.co.uk). This approach might not
 * accurately reflect all TLD variations and exceptions in the real world (such as .com,
 * .edu, or country-code TLDs that do not follow the two-part convention).
 *
 * @param {string} host The hostname from which to extract the top-level domain.
 *                      This can include subdomains and optionally a port number.
 * @returns {string} The extracted top-level domain. If the input does not conform to
 *                   a recognizable domain structure, returns the hostname without
 *                   the port number. If the hostname includes subdomains, returns
 *                   only the last two parts of the hostname as the TLD. For hostnames
 *                   without subdomains, returns the hostname itself.
 *
 * @example
 * extractTopLevelDomain('www.example.com:8080')
 * // Returns 'example.com'
 *
 * @example
 * extractTopLevelDomain('localhost:3000')
 * // Returns 'localhost'
 *
 * @example
 * extractTopLevelDomain('www.example.co.uk')
 * // Returns 'co.uk'
 */
export function extractTopLevelDomain(host: string) {
  // Remove port number if present
  const hostWithoutPort = host.split(':')[0] ?? host;

  // Split the host by dots
  const parts = hostWithoutPort.split('.');

  // For domains like .nl and .be, we can assume the TLD is the last two parts
  if (parts.length > 1) {
    return parts.slice(-2).join('.'); // Return the last 2 parts for the TLD
  }

  return hostWithoutPort; // Return the host as is if it doesn't have subdomains or for any edge cases
}

export function validateSlug(slug: string) {
  const regex = /^[a-z0-9-]+$/;
  return regex.test(slug);
}

export function getValidUuid(uuid: string | LocationQueryValue | LocationQueryValue[] | undefined) {
  if (typeof uuid !== 'string') {
    return null;
  }

  const uuidSchema = yup.string().uuid();
  const isValid = uuidSchema.isValidSync(uuid);

  if (isValid) {
    return uuid;
  }

  return null;
}

export function getDataAttr(category: string, action: string, label: string) {
  return {
    'data-analytics-category': category,
    'data-analytics-action': action,
    'data-analytics-label': label,
  };
}

/**
 *
 * @example valid slug: 'donald-duck-classics-pocket_263792'
 * @example valid slug: 'autoweek_48-2024~AUTOW00993'
 * @example valid slug: 'club-donald-duck-pocket-14~265076'
 * @example invalid slug: 'donald-duck-clasics-pocket'
 */
export function validateArticleRefSlug(slug: string) {
  const regex = /^[a-z0-9-]+(?:_[a-z0-9-]+)?(?:~[A-Z0-9-]+)?$/;
  return regex.test(slug);
}

/**
 *
 * @example valid slug: 'donald-duck-classics-pocket_263792'
 * @example valid slug: 'autoweek_48-2024~AUTOW00993
 * @example invalid slug: 'donald-duck-clasics-pocket'
 */
export function validateArticleRefNonSubscribableSlug(slug: string) {
  const regex = /^[a-z0-9-]+(~[A-Z0-9-]+)?$/;
  return regex.test(slug);
}

/**
 * Filters the url based on the blacklist
 *
 * @param url
 *
 * @example
 * ```TS
 * filterUrl('https://www.titel.nl?orderid=1234&amount=1234') // 'https://www.titel.nl'
 * filterUrl('https://www.titel.nl?orderid=1234&amount=1234&') // 'https://www.titel.nl'
 * ```
 */
export function filterUrl(url: string) {
  const blacklist = [
    'orderid',
    'currency',
    'amount',
    'pm',
    'acceptance',
    'status',
    'cardno',
    'ed',
    'cn',
    'schultz',
    'trxdate',
    'payid',
    'payidsub',
    'ncerror',
    'brand',
    'ipcty',
    'cccty',
    'eci',
    'cvccheck',
    'aavcheck',
    'vc',
    'dcc_indicator',
    'dcc_exchrate',
    'dcc_exchratets',
    'dcc_convccy',
    'dcc_convamount',
    'dcc_validhours',
    'dcc_exchratesource',
    'dcc_marginpercentage',
    'dcc_commpercentage',
    'ip',
    'txtcolor',
    'dcc_convshasign',
    'actiecode',
    'geslacht',
    'voorletters',
    'tussenvoegsel',
    'achternaam',
    'postcode',
    'huisnummer',
    'toevoeging',
    'straat',
    'plaats',
    'titel',
    'gender',
    'firstname',
    'lastnameprefix',
    'lastname',
    'phone',
    'zipcode',
    'housenumber',
    'housenumberaddition',
    'emailaddress',
    'versie',
    'gender',
    'actiecode',
    'vervolg_id',
  ];

  blacklist.forEach((val) => {
    val = val.toLowerCase();
    url = url.toLowerCase();
    const stringHasBlacklistItem = url.includes(val);
    let regex;
    if (stringHasBlacklistItem) {
      regex = `\\A?${val}=[^&]+&*`;

      const re = new RegExp(regex, 'g');
      url = url.replace(re, '');

      if (url.match(re) === null) {
        regex = `\\A?${val}=+&*`;
        const re = new RegExp(regex, 'g');
        url = url.replace(re, '');
      }
    }
  });

  return url;
}
